import {DateTime} from "luxon";
import type {Iso8601DateTime} from "@vesta/common-type-definitions/typeDefinitions";

/** Business calendar timezone for date-range filters and labels. */
export const BUSINESS_CALENDAR_ZONE = "Asia/Kolkata";

/** Convert the date picker's inclusive end instant into an exclusive end instant for SQL ranges. */
export function inclusiveIsoDateTimeToExclusiveIsoDateTime(value: Iso8601DateTime): Iso8601DateTime {
    const dateTime = DateTime.fromISO(value, {setZone: true});
    const exclusiveEnd = dateTime.plus({days: 1}).toISO();
    return (exclusiveEnd ?? value) as Iso8601DateTime;
}

/** Convert an inclusive calendar selection to canonical `[from, to)` timestamp bounds. */
export function inclusiveCalendarDateRangeToHalfOpenIsoBounds(args: {
    start: string;
    end: string;
}): {fromInclusive: Iso8601DateTime; toExclusive: Iso8601DateTime} | null {
    const startDay = parseCalendarDateFromIsoString(args.start);
    const endDay = parseCalendarDateFromIsoString(args.end);
    if (startDay === null || endDay === null) {
        return null;
    }

    const fromInclusive = startDay.startOf("day").toISO();
    const toExclusive = endDay.plus({days: 1}).startOf("day").toISO();
    if (fromInclusive === null || toExclusive === null) {
        return null;
    }

    return {
        fromInclusive: fromInclusive as Iso8601DateTime,
        toExclusive: toExclusive as Iso8601DateTime,
    };
}

/** Maps a clicked calendar cell to canonical `[from, to)` IST day bounds. */
export function calendarDayToFilterIsoBounds(date: Date): {
    rangeStart: Iso8601DateTime;
    rangeEnd: Iso8601DateTime;
} {
    const startDay = DateTime.fromJSDate(date, {zone: BUSINESS_CALENDAR_ZONE}).startOf("day");
    const exclusiveEndDay = startDay.plus({days: 1}).startOf("day");
    return {
        rangeStart: startDay.toISO() as Iso8601DateTime,
        rangeEnd: exclusiveEndDay.toISO() as Iso8601DateTime,
    };
}

/** Tightest overlapping `[from, to)` window; `null` when the ranges do not overlap. */
export function intersectHalfOpenIsoBounds(
    a: {rangeStart: Iso8601DateTime; rangeEnd: Iso8601DateTime},
    b: {rangeStart: Iso8601DateTime; rangeEnd: Iso8601DateTime},
): {rangeStart: Iso8601DateTime; rangeEnd: Iso8601DateTime} | null {
    const aStart = DateTime.fromISO(a.rangeStart, {setZone: true});
    const aEnd = DateTime.fromISO(a.rangeEnd, {setZone: true});
    const bStart = DateTime.fromISO(b.rangeStart, {setZone: true});
    const bEnd = DateTime.fromISO(b.rangeEnd, {setZone: true});
    if (aStart.isValid !== true || aEnd.isValid !== true || bStart.isValid !== true || bEnd.isValid !== true) {
        return null;
    }

    const start = aStart.toMillis() >= bStart.toMillis() ? aStart : bStart;
    const end = aEnd.toMillis() <= bEnd.toMillis() ? aEnd : bEnd;
    if (start.toMillis() >= end.toMillis()) {
        return null;
    }

    const rangeStart = start.toISO() as Iso8601DateTime;
    const rangeEnd = end.toISO() as Iso8601DateTime;
    return {
        rangeStart: rangeStart,
        rangeEnd: rangeEnd,
    };
}

/**
 * Maps FullCalendar's already-exclusive visible range to canonical `[from, to)` timestamp bounds.
 */
export function calendarVisibleRangeToFilterIsoBounds(args: {
    visibleRangeStart: Date;
    visibleRangeEnd: Date;
}): {
    rangeStart: Iso8601DateTime;
    rangeEnd: Iso8601DateTime;
} {
    const startDay = DateTime.fromJSDate(args.visibleRangeStart, {zone: BUSINESS_CALENDAR_ZONE}).startOf("day");
    let lastVisibleDay = DateTime.fromJSDate(args.visibleRangeEnd, {zone: BUSINESS_CALENDAR_ZONE});

    if (lastVisibleDay.equals(lastVisibleDay.startOf("day")) === true && lastVisibleDay > startDay) {
        lastVisibleDay = lastVisibleDay.minus({days: 1}).startOf("day");
    } else {
        lastVisibleDay = lastVisibleDay.startOf("day");
    }
    const exclusiveEndDay = lastVisibleDay.plus({days: 1}).startOf("day");

    return {
        rangeStart: startDay.toISO() as Iso8601DateTime,
        rangeEnd: exclusiveEndDay.toISO() as Iso8601DateTime,
    };
}

/** Parse the calendar day from an ISO string (`YYYY-MM-DD` prefix), ignoring clock time/offset. */
export function parseCalendarDateFromIsoString(isoString: string): DateTime | null {
    if (isoString.length < 10) {
        return null;
    }
    const datePart = isoString.slice(0, 10);
    const dt = DateTime.fromFormat(datePart, "yyyy-MM-dd", {zone: BUSINESS_CALENDAR_ZONE});
    return dt.isValid === true ? dt : null;
}

/** Format a calendar date for range pills (avoids UTC end-of-day showing as the next day in IST). */
export function formatCalendarDateIsoSegment(isoString: string, includeYear: boolean): string {
    const dt = parseCalendarDateFromIsoString(isoString);
    if (dt === null) {
        return "-";
    }
    return includeYear === true ? dt.toFormat("d LLL yyyy") : dt.toFormat("d LLL");
}
